import { useState } from "react";
import { Link } from "react-router-dom";
import { Apple, ArrowUpRight, Check, Copy, Smartphone } from "lucide-react";
import { Card, Eyebrow, SegmentedNav } from "@/ui";
import { useWorkspace } from "@/app/WorkspaceContext";
import { SampleChip } from "@/screens/you/parts";

/* -------------------------------------------------------------------------- */
/*  Mobile setup guide — honest edition.                                       */
/*                                                                             */
/*  aigen does NOT ship a proprietary native SDK. It runs a PostHog-compatible  */
/*  ingestion endpoint, so you install the OFFICIAL, open-source PostHog mobile */
/*  SDK (MIT) and point it at your workspace: host = <origin>/analytics/v1/ph/  */
/*  <site_id>, apiKey = your aigen write key. This mirrors builder-ui's         */
/*  InstallSnippets (the source of truth) and the verified PostHog SDK API.     */
/* -------------------------------------------------------------------------- */

type Platform = "ios" | "android";

/** The host an SDK should target — same convention as builder-ui's snippets. */
function ingestHost(): string {
  return typeof window !== "undefined" ? window.location.origin : "https://your-workspace.example";
}

/** Copyable code block (same affordance as Integrations' InstallSnippets). */
function CodeBlock({ label, code }: { label: string; code: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="overflow-hidden rounded-btn border border-hairline">
      <div className="flex items-center justify-between border-b border-hairline bg-paper-2 px-3 py-2 dark:bg-white/5">
        <span className="eyebrow">{label}</span>
        <button
          onClick={() => {
            void navigator.clipboard?.writeText(code);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1400);
          }}
          className="inline-flex items-center gap-1 font-mono text-[11px] text-subtle hover:text-ink"
          aria-label="Copy snippet"
        >
          {copied ? (
            <>
              <Check className="h-3.5 w-3.5 text-emerald-600" strokeWidth={2} /> copied
            </>
          ) : (
            <>
              <Copy className="h-3.5 w-3.5" strokeWidth={1.5} /> copy
            </>
          )}
        </button>
      </div>
      <pre className="overflow-x-auto px-3 py-3 font-mono text-[12px] leading-relaxed text-ink">
        <code>{code}</code>
      </pre>
    </div>
  );
}

/** A numbered step block: title + optional blurb + children (code / notes). */
function Step({
  n,
  title,
  children,
  blurb,
}: {
  n: number;
  title: string;
  blurb?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-4">
      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full border border-hairline font-mono text-[12px] text-muted">
        {n}
      </span>
      <div className="min-w-0 flex-1 space-y-2.5">
        <div>
          <h3 className="text-[15px] font-semibold tracking-tight text-ink-strong">{title}</h3>
          {blurb && <p className="mt-0.5 text-[13px] leading-relaxed text-muted">{blurb}</p>}
        </div>
        {children}
      </div>
    </div>
  );
}

/** A calm informational note line. */
function Note({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded-btn border border-hairline bg-paper-2 px-3 py-2 text-[12px] leading-relaxed text-subtle dark:bg-white/5">
      {children}
    </p>
  );
}

/* ------------------------------ code snippets ----------------------------- */
/*  iOS — official PostHog SDK (https://github.com/PostHog/posthog-ios, MIT).  */

const iosInstallSpm = `// Xcode → File → Add Package Dependencies…
// Paste this URL (official PostHog SDK, MIT):
https://github.com/PostHog/posthog-ios.git

// …or add it to Package.swift (requires posthog-ios >= 3.6.0):
dependencies: [
  .package(url: "https://github.com/PostHog/posthog-ios.git", from: "3.6.0")
]`;

const iosInstallPods = `# Podfile
pod 'PostHog', '~> 3.6'

# then
$ pod install`;

function iosInit(host: string, siteId: string, last4: string): string {
  return `import PostHog

// Call once on launch (AppDelegate / @main App init).
// apiKey  = your aigen WRITE KEY (ends …${last4}; shown once at creation — rotate to reveal).
// host    = your aigen workspace; the {site_id} path resolves the property server-side.
let config = PostHogConfig(
  apiKey: "YOUR_WRITE_KEY",
  host: "${host}/analytics/v1/ph/${siteId}")
PostHogSDK.shared.setup(config)`;
}

const iosTrack = `PostHogSDK.shared.capture("signup_completed", properties: [
  "plan": "pro",
  "referrer": "launch"
])`;

const iosScreen = `// Screen views are captured automatically (config.captureScreenViews, default true).
// Manual (SwiftUI / custom flows):
PostHogSDK.shared.screen("Checkout", properties: ["step": "payment"])`;

const iosIdentify = `PostHogSDK.shared.identify("user_8d21", userProperties: [
  "email": "ada@example.com",
  "plan": "pro"
])`;

const iosReplay = `// Add these to the config from step 2, BEFORE setup():
config.sessionReplay = true                          // record native session replay
config.sessionReplayConfig.maskAllTextInputs = true  // default true
config.sessionReplayConfig.maskAllImages = true      // default true
config.sessionReplayConfig.screenshotMode = false    // set true for SwiftUI apps`;

const iosOptOut = `// Honour consent — stops all capture (events + replay), persisted across launches.
PostHogSDK.shared.optOut()

// Resume later:
PostHogSDK.shared.optIn()`;

/*  Android — official PostHog SDK (https://github.com/PostHog/posthog-android, MIT). */

const androidInstallGradle = `// settings.gradle.kts → dependencyResolutionManagement
repositories {
  mavenCentral()
}

// app/build.gradle.kts → dependencies  (official PostHog SDK, MIT)
implementation("com.posthog:posthog-android:3.+")`;

function androidInit(host: string, siteId: string, last4: string): string {
  return `import com.posthog.android.PostHogAndroid
import com.posthog.android.PostHogAndroidConfig

// Application.onCreate() — call once on launch.
// apiKey = your aigen WRITE KEY (ends …${last4}; shown once at creation — rotate to reveal).
// host   = your aigen workspace; the {site_id} path resolves the property server-side.
val config = PostHogAndroidConfig(
  apiKey = "YOUR_WRITE_KEY",
  host = "${host}/analytics/v1/ph/${siteId}")
PostHogAndroid.setup(applicationContext, config)`;
}

const androidTrack = `PostHog.capture("signup_completed", properties = mapOf(
  "plan" to "pro",
  "referrer" to "launch"
))`;

const androidScreen = `// Screen views are captured automatically (captureScreenViews, default true).
// Manual (Compose / custom flows):
PostHog.screen("Checkout", properties = mapOf("step" to "payment"))`;

const androidIdentify = `PostHog.identify("user_8d21", userProperties = mapOf(
  "email" to "ada@example.com",
  "plan" to "pro"
))`;

function androidReplay(host: string, siteId: string): string {
  return `// Enable replay on the config from step 2:
val config = PostHogAndroidConfig(
  apiKey = "YOUR_WRITE_KEY",
  host = "${host}/analytics/v1/ph/${siteId}"
).apply {
  sessionReplay = true                       // record native session replay
  sessionReplayConfig.maskAllTextInputs = true
  sessionReplayConfig.maskAllImages = true
  sessionReplayConfig.screenshot = false     // true => screenshot mode (Compose)
}
PostHogAndroid.setup(applicationContext, config)`;
}

const androidOptOut = `// Honour consent — stops all capture (events + replay).
PostHog.optOut()

// Resume later:
if (PostHog.isOptOut()) PostHog.optIn()`;

/* ------------------------------- platform copy ---------------------------- */

interface PlatformContent {
  langLabel: string;
  pkgLabel: string;
  install: { label: string; code: string }[];
  init: (host: string, siteId: string, last4: string) => string;
  track: string;
  screen: string;
  identify: string;
  replay: (host: string, siteId: string) => string;
  optOut: string;
}

const CONTENT: Record<Platform, PlatformContent> = {
  ios: {
    langLabel: "Swift",
    pkgLabel: "posthog-ios · 3.6+",
    install: [
      { label: "Swift Package Manager", code: iosInstallSpm },
      { label: "CocoaPods", code: iosInstallPods },
    ],
    init: iosInit,
    track: iosTrack,
    screen: iosScreen,
    identify: iosIdentify,
    replay: () => iosReplay,
    optOut: iosOptOut,
  },
  android: {
    langLabel: "Kotlin",
    pkgLabel: "posthog-android · 3.+",
    install: [{ label: "Gradle (Kotlin DSL)", code: androidInstallGradle }],
    init: androidInit,
    track: androidTrack,
    screen: androidScreen,
    identify: androidIdentify,
    replay: androidReplay,
    optOut: androidOptOut,
  },
};

const PLATFORM_TABS: { value: Platform; label: string }[] = [
  { value: "ios", label: "iOS · Swift" },
  { value: "android", label: "Android · Kotlin" },
];

/* --------------------------------- screen --------------------------------- */

export function SdkScreen() {
  const ws = useWorkspace();
  const current = ws.current;
  const site = current?.site;
  const kind = current?.kind;
  const isMobileProperty = kind === "ios" || kind === "android";

  // Default the active tab to the current property's platform when it's a
  // mobile app; otherwise start on iOS.
  const [platform, setPlatform] = useState<Platform>(
    kind === "android" ? "android" : "ios",
  );

  // Use the live property's real keys only when it IS a mobile app; otherwise a
  // clearly-labelled sample so web/built users still see a working example.
  const useReal = ws.live && isMobileProperty && !!site;
  const siteId = useReal ? site!.site_id : "site_SAMPLE0000";
  const last4 = useReal ? site!.write_key_last4 : "9f2a";
  const host = ingestHost();

  const c = CONTENT[platform];

  return (
    <div className="stagger space-y-7">
      {/* header ----------------------------------------------------------- */}
      <header className="space-y-3">
        <Eyebrow>Developers · Mobile analytics</Eyebrow>
        <h1 className="text-[26px] font-semibold tracking-tight text-ink-strong md:text-[32px]">
          Connect your mobile app
        </h1>
        <p className="max-w-2xl text-[14px] leading-relaxed text-muted">
          Capture events, screens, identified users and full native session replay — using the
          official <span className="font-medium text-ink">PostHog SDK</span> (open-source, MIT),
          sent straight to your aigen workspace. The same Signals you get on the web.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-chip bg-paper-2 px-2.5 py-1 font-mono text-[11px] text-muted dark:bg-white/5">
            <Apple className="h-3.5 w-3.5" strokeWidth={1.75} /> iOS
          </span>
          <span className="text-subtle">·</span>
          <span className="inline-flex items-center gap-1.5 rounded-chip bg-paper-2 px-2.5 py-1 font-mono text-[11px] text-muted dark:bg-white/5">
            <Smartphone className="h-3.5 w-3.5" strokeWidth={1.75} /> Android
          </span>
          <span className="ml-1 text-[12px] text-subtle">via the PostHog SDK (MIT).</span>
        </div>
      </header>

      {/* why posthog ------------------------------------------------------ */}
      <Note>
        aigen runs a <span className="font-medium text-ink">PostHog-compatible</span> ingestion
        endpoint, so there's no proprietary SDK to vendor — you install the official open-source
        PostHog mobile SDK and point its <span className="font-mono text-[11px]">host</span> at your
        workspace. Please include the PostHog MIT license in your app's “Open Source Licenses”
        screen.
      </Note>

      {/* keys banner ------------------------------------------------------ */}
      <Card className="flex flex-wrap items-center justify-between gap-3 p-5">
        <div className="min-w-0">
          <p className="eyebrow mb-1 flex items-center gap-2">
            Your credentials {!useReal && <SampleChip />}
          </p>
          <p className="text-[13px] text-muted">
            host <span className="font-mono text-[12px] text-ink-strong">{host}/analytics/v1/ph/{siteId}</span>
          </p>
          <p className="mt-0.5 text-[13px] text-muted">
            apiKey = write key <span className="font-mono text-[12px] text-ink-strong">····{last4}</span>
          </p>
        </div>
        <p className="max-w-xs text-[12px] leading-relaxed text-subtle">
          {useReal
            ? `Keyed to “${current?.name}”. Your full write key is shown once when the property is created — rotate it to reveal a fresh one.`
            : "Sample IDs — select (or create) a mobile property to generate its real site ID and write key."}
        </p>
      </Card>

      {/* web note --------------------------------------------------------- */}
      <Note>
        Tracking a website instead? Web uses a one-line script tag (the first-party{" "}
        <span className="font-mono text-[11px]">dl.js</span>), not this SDK — grab it under{" "}
        <Link to="/you/integrations" className="font-medium text-ink underline-offset-2 hover:underline">
          You → Integrations
        </Link>
        .
      </Note>

      {/* platform tabs ---------------------------------------------------- */}
      <div className="space-y-5">
        <div className="flex items-center justify-between gap-3 border-b border-hairline pb-1">
          <SegmentedNav<Platform> items={PLATFORM_TABS} value={platform} onChange={setPlatform} />
          <span className="hidden font-mono text-[11px] text-subtle sm:inline">{c.pkgLabel}</span>
        </div>

        <Card className="space-y-7 p-5 md:p-6">
          <Step
            n={1}
            title="Install the PostHog SDK"
            blurb={
              platform === "ios"
                ? "Add the official PostHog package with Swift Package Manager (recommended) or CocoaPods."
                : "Add the official PostHog dependency from Maven Central via Gradle."
            }
          >
            <div className="space-y-3">
              {c.install.map((b) => (
                <CodeBlock key={b.label} label={b.label} code={b.code} />
              ))}
            </div>
          </Step>

          <Step
            n={2}
            title="Initialize on launch"
            blurb="Call setup() once, as early as possible — in your app's entry point. apiKey is your aigen write key; host points the SDK at your workspace's ingestion."
          >
            <CodeBlock label={`Initialize — ${c.langLabel}`} code={c.init(host, siteId, last4)} />
            {!useReal && (
              <Note>
                The site ID above is a sample. Swap in your mobile property's real values once it
                exists, and replace <span className="font-mono text-[11px]">YOUR_WRITE_KEY</span>{" "}
                with the key shown at creation.
              </Note>
            )}
          </Step>

          <Step
            n={3}
            title="Track events"
            blurb="Record any meaningful action with a name and optional properties. These flow straight into Signals' funnels and retention."
          >
            <CodeBlock label={`Track an event — ${c.langLabel}`} code={c.track} />
          </Step>

          <Step
            n={4}
            title="Screen / view tracking"
            blurb="Screen views are captured automatically. For custom flows (SwiftUI / Compose) or to override names, call screen() manually."
          >
            <CodeBlock label={`Screen tracking — ${c.langLabel}`} code={c.screen} />
          </Step>

          <Step
            n={5}
            title="Identify users"
            blurb="Tie events to a known user once they sign in. User properties enrich profiles and segments; call identify() again to update them."
          >
            <CodeBlock label={`Identify — ${c.langLabel}`} code={c.identify} />
          </Step>

          <Step
            n={6}
            title="Enable session replay"
            blurb="Capture native screens as privacy-safe wireframes and watch them back in Signals → Replays. Text inputs and images are masked by default."
          >
            <CodeBlock label={`Session replay — ${c.langLabel}`} code={c.replay(host, siteId)} />
            <Note>
              Mobile replays are recorded as structured wireframes (not pixel video), so they stay
              small and private. aigen renders them in the same player as web replays via the
              vendored PostHog mobile→rrweb transformer.
            </Note>
          </Step>

          <Step
            n={7}
            title="Privacy & opt-out"
            blurb="Respect consent and regional rules. optOut() halts all capture immediately and is persisted across launches until you opt back in."
          >
            <CodeBlock label={`Consent — ${c.langLabel}`} code={c.optOut} />
            <Note>
              No PII is collected unless you pass it. Masking is on by default for inputs and images;
              you can purge a user from <span className="font-mono text-[11px]">You → Privacy</span>{" "}
              at any time.
            </Note>
          </Step>
        </Card>

        {/* footer link -------------------------------------------------- */}
        <div className="flex items-center justify-between rounded-card border border-dashed border-hairline px-5 py-4">
          <div>
            <p className="text-[13px] font-medium text-ink-strong">Already installed?</p>
            <p className="text-[12px] text-muted">Watch events land live in Signals.</p>
          </div>
          <Link
            to="/signals"
            className="inline-flex items-center gap-1 text-[13px] font-medium text-ink hover:text-accent"
          >
            Open Signals <ArrowUpRight className="h-4 w-4" strokeWidth={1.75} />
          </Link>
        </div>
      </div>
    </div>
  );
}
